import React, { useContext, useEffect, useRef, useState } from 'react';
import { HUBBLE_SESSION_AUTH } from '../../modules/Login';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { AppContext } from '../../modules/AppContext';
import Session from '../../modules/Session';

/**
 * A React component that establishes and manages a SignalR connection to a specific hub group.
 * Handles authentication, automatic reconnection, and real-time event listening.
 *
 * @component
 * @param {Object} props
 * @param {string} props.hubUrl - SignalR hub endpoint URL
 * @param {string} props.hubName - Hub display name for logging
 * @param {Object} props.hubGroup - Group configuration object
 * @param {string} props.hubGroup.name - Group display name
 * @param {string} props.hubGroup.joinGroupMethodName - Server method to join the group
 * @param {Object} props.hubGroup.listenerMethods - Available SignalR methods for this group
 * @param {Array<Object>} props.listeners - Event listeners to register
 * @param {string} props.listeners[].name - SignalR method name to listen for
 * @param {Function} props.listeners[].method - Callback function for the event
 *
 * @example
 * // 1. Define SignalR configuration (ideally in a service module like andromedaService.js)
 * // These method listeners often correspond to entities fetched from the given module
 * // This example is from the andromeda module
 * const signalRConfig = {
 *   hubName: 'Andromeda',
 *   hubUrl: 'https://api.example.com/hubs/andromeda',
 *   hubGroups: {
 *     agentUpdates: {
 *       name: 'Andromeda Agent Updates',
 *       joinGroupMethodName: 'JoinAndromedaAgentUpdatesGroup',
 *       listenerMethods: {
 *         createAgent: 'CreateAgent',
 *         updateAgent: 'UpdateAgent',
 *       },
 *     }
 *   }
 * };
 *
 * // 2. Destructure config and create listeners
 * const {
 *   getAgents,
 *   signalRConfig: {
 *     hubUrl,
 *     hubName,
 *     hubGroups: { agentUpdates: hubGroup },
 *   },
 * } = useAndromeda();
 *
 * const listeners = [
 *   {
 *     name: hubGroup.listenerMethods.updateAgent,
 *     method: (agent) => setAgents(prev => prev.map(a => a.id === agent.id ? agent : a))
 *   }
 * ];
 *
 * // 3. Use component
 * <SignalRHubGroupListener
 *   hubUrl={hubUrl}
 *   hubName={hubName}
 *   hubGroup={hubGroup}
 *   listeners={listeners}
 * />
 *
 * @returns {React.ReactElement} Empty fragment - connection management only
 */
export const SignalRHubGroupListener = ({
  hubUrl,
  hubName,
  hubGroup,
  hubListeners,
}) => {
  const { showToast } = useContext(AppContext);

  const isCleanupRef = useRef(null);
  const connectionRef = useRef(null);

  useEffect(() => {
    const connectToHub = async () => {
      try {
        const newConnection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => {
              const { token } = Session.get(HUBBLE_SESSION_AUTH) || {};
              return token;
            },
          })
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect()
          .build();

        await newConnection.start();
        await newConnection.invoke(hubGroup.joinGroupMethodName);
        console.info(`Joined the ${hubGroup.name} group`);

        hubListeners.map((listener) =>
          newConnection.on(listener.name, listener.method)
        );

        newConnection.onreconnecting((error) => {
          const msg = `Connection lost. Attempting to reconnect...`;
          console.error(`${msg} :: ${error}`);
          showToast({
            message: msg,
            type: 'error',
            duration: 50000,
          });
        });

        newConnection.onreconnected(async () => {
          console.info(`Reconnected to ${hubName} hub`);
          await newConnection.invoke(hubGroup.joinGroupMethodName);
          console.info(`Joined the ${hubGroup.name} group`);

          showToast({
            message: 'Connection re-established.',
            type: 'success',
          });
        });

        newConnection.onclose(() => {
          if (isCleanupRef.current) return;
          const msg = `Connection lost. Please refresh the page to restart the connection.`;
          console.error(msg);
          showToast({
            message: msg,
            type: 'error',
            duration: 600000,
          });
        });

        connectionRef.current = newConnection;
      } catch (error) {
        console.error(`Error connecting to ${hubName} hub:`, error);
      }
    };

    connectToHub();

    return () => {
      if (connectionRef.current) {
        isCleanupRef.current = true;
        connectionRef.current
          .stop()
          .then(() => console.log(`Disconnected from ${hubName} hub`));
      }
    };
  }, []);

  return <></>;
};
